import type { BoxOfferRulePeriod, OfferAutonomousPurchase } from "@/modules/boxOffer/entities/boxOfferTypes"
import { CoachOfferPriceEntity } from "@/modules/coachOffers/entities/coachOfferPriceEntity"
import type { CoachOfferType } from "@/modules/coachOffers/entities/coachOfferTypes"
import type { CoachOfferFormValues } from "@/modules/coachOffers/types/coachOfferFormTypes"
import { word } from "@/modules/core/utils/i18n"
import { type CoachOfferDetailsDto } from "@modules/coachOffers/gateway/coachOffersDto"
import { MediaType } from "@modules/common/media/mediaTypes"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import type { HUImage } from "@modules/types/imageTypes"

export type CoachOfferRule = {
  value: number
  period: BoxOfferRulePeriod
}

export class CoachOfferEntity {
  id: string
  type: CoachOfferType
  name: string
  image: HUImage | null
  description: string | null
  renewable: boolean | null
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: OfferAutonomousPurchase
  archived: boolean
  displayOrder: number | null
  paymentOffers: CoachOfferPriceEntity[]
  programs: string[]
  rules: CoachOfferRule[]

  private constructor(offer: Omit<CoachOfferEntity, "getReservationRulesText" | "fromEntityToFormValues">) {
    this.id = offer.id
    this.type = offer.type
    this.name = offer.name
    this.image = offer.image
    this.description = offer.description
    this.renewable = offer.renewable
    this.showcaseDisplay = offer.showcaseDisplay
    this.priceDisplay = offer.priceDisplay
    this.autonomousPurchase = offer.autonomousPurchase
    this.archived = offer.archived
    this.displayOrder = offer.displayOrder
    this.paymentOffers = offer.paymentOffers
    this.programs = offer.programs
    this.rules = offer.rules
  }

  static fromDto(dto: CoachOfferDetailsDto): CoachOfferEntity {
    return new CoachOfferEntity({
      id: dto.id,
      type: dto.type,
      name: dto.name,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      description: dto.description,
      renewable: dto.renewable,
      showcaseDisplay: dto.showcaseDisplay,
      priceDisplay: dto.priceDisplay,
      autonomousPurchase: dto.autonomousPurchase,
      archived: dto.archived,
      displayOrder: dto.displayOrder,
      programs: dto.programs,
      rules: dto.rules,
      paymentOffers: dto.paymentOffers.map((paymentOffer) => CoachOfferPriceEntity.fromDto(paymentOffer)),
    })
  }

  getReservationRulesText(): string[] {
    if (this.rules.length === 0) return []

    return this.rules.map((rule) => {
      const periodMap = {
        DAY: word("global.day.label", { count: 1 }),
        WEEK: word("global.week.label", { count: 1 }),
        MONTH: word("global.month.label", { count: 1 }),
      }
      const period = periodMap[rule.period]
      return `${rule.value.toString()}x/${period}`
    })
  }

  fromEntityToFormValues(): CoachOfferFormValues {
    return {
      type: this.type,
      name: this.name,
      description: this.description ?? "",
      rules: this.rules,
      categoryIds: this.programs,
      showcaseDisplay: this.showcaseDisplay,
      priceDisplay: this.priceDisplay,
      autonomousPurchase: this.autonomousPurchase,
      paymentOffers: this.paymentOffers.map((offer) => offer.fromEntityToFormValues()),
      defaultHideSessionContent: false,
      image: this.image
        ? {
            url: this.image.url,
            key: this.image.key ?? null,
            type: MediaType.S3_IMAGE,
            filename: null,
            isUploading: false,
            file: null,
          }
        : null,
    }
  }
}
